/* FNDRY — ingredient research tabs */
(function () {
  'use strict';

  var SELECTOR = '[data-fndry-ingredients]';
  var LEAVE_MS = 180;
  var SCROLL_MS = 850; /* mobile page autoscroll */
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileMQ = window.matchMedia('(max-width: 749px)');

  /* --fndry-header-h may be authored in px or rem; the root is 10px here. */
  function cssLength(value, fallback) {
    if (!value) return fallback;
    var raw = String(value).trim();
    var n = parseFloat(raw);
    if (isNaN(n)) return fallback;
    if (raw.indexOf('rem') > -1) {
      var root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      return n * root;
    }
    return n;
  }

  function headerHeight() {
    var v = getComputedStyle(document.documentElement).getPropertyValue('--fndry-header-h');
    return cssLength(v, mobileMQ.matches ? 68 : 88);
  }

  /* Native `behavior: smooth` runs at a fixed ~400ms with no way to slow it,
     so the page scroll is tweened here instead. */
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function tweenScroll(to, duration) {
    var from = window.pageYOffset;
    var distance = to - from;
    if (!distance) return;
    if (reduceMQ.matches || !window.requestAnimationFrame) {
      window.scrollTo(0, to);
      return;
    }

    var start = null;
    var cancelled = false;

    function abort() {
      cancelled = true;
    }
    /* never fight the user */
    window.addEventListener('wheel', abort, { passive: true, once: true });
    window.addEventListener('touchstart', abort, { passive: true, once: true });

    function step(now) {
      if (cancelled) return;
      if (start === null) start = now;
      var t = Math.min((now - start) / duration, 1);
      window.scrollTo(0, from + distance * easeInOutCubic(t));
      if (t < 1) window.requestAnimationFrame(step);
      else {
        window.removeEventListener('wheel', abort);
        window.removeEventListener('touchstart', abort);
      }
    }
    window.requestAnimationFrame(step);
  }

  function IngredientTabs(root) {
    if (root.dataset.fndryIngredientsReady === 'true') return;
    root.dataset.fndryIngredientsReady = 'true';

    var rail = root.querySelector('[data-fi-rail]');
    var tabsWrap = root.querySelector('[data-fi-tabs]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-fi-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-fi-panel]'));
    if (!panels.length) return;

    var current = 0;
    for (var i = 0; i < panels.length; i++) {
      if (!panels[i].hidden) {
        current = i;
        break;
      }
    }
    var leaveTimer = null;

    /* ---------- rail ---------- */

    function centreTab(tab, smooth) {
      if (!rail || !tab) return;
      if (rail.scrollWidth - rail.clientWidth < 2) return;
      var railBox = rail.getBoundingClientRect();
      var tabBox = tab.getBoundingClientRect();
      var delta = tabBox.left - railBox.left - (railBox.width - tabBox.width) / 2;
      var left = Math.max(0, Math.min(rail.scrollLeft + delta, rail.scrollWidth - rail.clientWidth));
      if (Math.abs(left - rail.scrollLeft) < 1) return;
      if (typeof rail.scrollTo === 'function') {
        rail.scrollTo({ left: left, behavior: smooth && !reduceMQ.matches ? 'smooth' : 'auto' });
      } else {
        rail.scrollLeft = left;
      }
    }

    /* Pull the page up so the tab rail sits at the top of the viewport,
       section heading already scrolled past. Mobile only. */
    function scrollToRail() {
      if (!tabsWrap) return;
      var top = window.pageYOffset + tabsWrap.getBoundingClientRect().top - headerHeight() - 12;
      top = Math.max(0, top);
      if (Math.abs(top - window.pageYOffset) < 8) return;
      tweenScroll(top, SCROLL_MS);
    }

    /* ---------- panels ---------- */

    function play(panel, mode) {
      panel.removeAttribute('data-enter');
      void panel.offsetWidth; /* restart the animation */
      panel.setAttribute('data-enter', mode);
    }

    function commit(index, mode) {
      panels.forEach(function (panel, i) {
        panel.classList.remove('is-leaving');
        panel.hidden = i !== index;
      });
      current = index;
      play(panels[index], mode);
    }

    function setTabState(index) {
      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
    }

    function swap(index, mode) {
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }
      if (index === current) {
        play(panels[index], mode);
        return;
      }
      if (reduceMQ.matches) {
        commit(index, mode);
        return;
      }
      panels[current].classList.add('is-leaving');
      leaveTimer = setTimeout(function () {
        leaveTimer = null;
        commit(index, mode);
      }, LEAVE_MS);
    }

    function activate(index, source) {
      if (index < 0 || index >= panels.length) return;
      setTabState(index);
      centreTab(tabs[index], source !== 'init');
      if (source === 'init') return;

      var onMobile = mobileMQ.matches;
      if (source === 'pointer' && onMobile) scrollToRail();
      swap(index, onMobile ? 'rise' : 'fade');
    }

    /* ---------- events ---------- */

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        activate(index, 'pointer');
      });

      tab.addEventListener('keydown', function (event) {
        var key = event.key;
        var next = null;
        if (key === 'ArrowRight' || key === 'ArrowDown') next = (index + 1) % tabs.length;
        else if (key === 'ArrowLeft' || key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
        else if (key === 'Home') next = 0;
        else if (key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        activate(next, 'key');
        tabs[next].focus({ preventScroll: true });
        centreTab(tabs[next], true);
      });
    });

    /* Theme editor: selecting a block switches to its tab */
    function onBlockSelect(event) {
      if (!document.body.contains(root)) return;
      var id = event.detail && event.detail.blockId;
      if (!id) return;
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].dataset.blockId === id) {
          setTabState(i);
          centreTab(tabs[i], false);
          swap(i, 'fade');
          return;
        }
      }
    }
    document.addEventListener('shopify:block:select', onBlockSelect);

    var resizeTimer = null;
    /* P15 sitewide 2026-10-23: a resize that keeps the width is the phone
       toolbar collapsing or coming back - the tab rail does not depend on it. */
    var railWidth = document.documentElement.clientWidth;
    window.addEventListener(
      'resize',
      function () {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          var w = document.documentElement.clientWidth;
          if (w === railWidth) return;
          railWidth = w;
          centreTab(tabs[current], false);
        }, 150);
      },
      { passive: true }
    );

    /* ---------- scroll reveal ---------- */

    function reveal() {
      if (root.dataset.reveal === 'in') return;
      root.dataset.reveal = 'in';
      play(panels[current], 'reveal');
    }

    /* Reset once the section has left the viewport downwards, so it plays
       again the next time it is scrolled to instead of firing once on load. */
    function rearm() {
      if (root.dataset.reveal === 'pending') return;
      root.dataset.reveal = 'pending';
      panels.forEach(function (panel) {
        panel.removeAttribute('data-enter');
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) reveal();
            else if (entry.boundingClientRect.top > 0) rearm();
          });
        },
        /* fires once the section is a fifth of the way up the viewport */
        { threshold: 0, rootMargin: '0px 0px -20% 0px' }
      );
      io.observe(root);
    } else {
      reveal();
    }

    activate(current, 'init');
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var found = Array.prototype.slice.call(context.querySelectorAll(SELECTOR));
    if (context.matches && context.matches(SELECTOR)) found.unshift(context);
    found.forEach(function (el) {
      try {
        new IngredientTabs(el);
      } catch (error) {
        /* never leave the copy stranded at opacity 0 */
        el.dataset.reveal = 'in';
        if (window.console) console.error('[fndry-ingredients]', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();